import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { NavSegmentOption } from '@/components/common/NavSegmentControl';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getNavigationOptions, getNavigationValueFromPathname, isSubNavigationPathname, NAVIGATION_VALUE_FIXED_RATE, NAVIGATION_VALUE_PORTFOLIO } from '@/helpers/routing';
import { usePortfolioConnectGate } from '@/hooks/usePortfolioConnectGate';
import { useUserPositionsCount } from '@/hooks/useUserPositionsCount';
import { useTrackFixedRateNavViewed } from '@/lib/analytics/client/hooks/useTrackFixedRateNavViewed';
import { trackFixedRateAppClicked } from '@/lib/analytics/client/trackers/fixed-rate';
import { trackPortfolioNavTabClicked } from '@/lib/analytics/client/trackers/portfolio';
export interface UseNavigationOptions {
  navigationValue: string;
  onNavigationValueChange: (value: string) => void;
  navigationOptions: NavSegmentOption<string>[];
  mobileSubNavigationOptions: NavSegmentOption<string>[];
  shouldDisplaySubNavigation: boolean;
}

/**
 * Hook that manages navigation options and state for the app's main navigation.
 * Surfaces the connected wallet's position count as a badge on the Portfolio tab.
 * @returns Navigation state, options, and mobile sub-navigation config
 */
export const useNavigationOptions = () => {
  const $ = _c(23);
  const pathname = usePathname();
  const {
    account
  } = useWeb3Context();
  const userAddress = account.isConnected ? account.address : undefined;
  const positionCount = useUserPositionsCount();
  const openPortfolioConnectModal = usePortfolioConnectGate();
  let t0;
  if ($[0] !== pathname) {
    t0 = getNavigationValueFromPathname(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [navigationValue, setNavigationValue] = useState(t0);
  useTrackFixedRateNavViewed();
  let t1;
  if ($[2] !== positionCount || $[3] !== userAddress) {
    t1 = getNavigationOptions({
      positionCount,
      userAddress
    });
    $[2] = positionCount;
    $[3] = userAddress;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const navigationOptions = t1;
  let t2;
  if ($[5] !== navigationOptions) {
    t2 = navigationOptions.filter(_temp);
    $[5] = navigationOptions;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const mobileSubNavigationOptions = t2;
  let t3;
  if ($[7] !== mobileSubNavigationOptions || $[8] !== pathname) {
    t3 = isSubNavigationPathname(pathname, mobileSubNavigationOptions.map(_temp2));
    $[7] = mobileSubNavigationOptions;
    $[8] = pathname;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const shouldDisplaySubNavigation = t3;
  let t4;
  let t5;
  if ($[10] !== pathname) {
    t4 = () => {
      setNavigationValue(getNavigationValueFromPathname(pathname));
    };
    t5 = [pathname];
    $[10] = pathname;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== openPortfolioConnectModal || $[14] !== positionCount || $[15] !== userAddress) {
    t6 = value_0 => {
      if (value_0 === NAVIGATION_VALUE_FIXED_RATE) {
        trackFixedRateAppClicked({
          source: "topnav"
        });
        return;
      }
      if (value_0 === NAVIGATION_VALUE_PORTFOLIO) {
        trackPortfolioNavTabClicked({
          source: "navbar",
          positionCount
        });
        if (!userAddress) {
          openPortfolioConnectModal();
          return;
        }
      }
      setNavigationValue(value_0);
    };
    $[13] = openPortfolioConnectModal;
    $[14] = positionCount;
    $[15] = userAddress;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const onNavigationValueChange = t6;
  let t7;
  if ($[17] !== mobileSubNavigationOptions || $[18] !== navigationOptions || $[19] !== navigationValue || $[20] !== onNavigationValueChange || $[21] !== shouldDisplaySubNavigation) {
    t7 = {
      navigationValue,
      navigationOptions,
      mobileSubNavigationOptions,
      shouldDisplaySubNavigation,
      onNavigationValueChange
    };
    $[17] = mobileSubNavigationOptions;
    $[18] = navigationOptions;
    $[19] = navigationValue;
    $[20] = onNavigationValueChange;
    $[21] = shouldDisplaySubNavigation;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
};
function _temp(t0) {
  const {
    isMobileSubNavigation
  } = t0;
  return isMobileSubNavigation;
}
function _temp2(t0) {
  const {
    value
  } = t0;
  return value;
}